import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, Plus, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  getCatalogoExamesControllerObterQueryKey,
  useCatalogoExamesControllerReconciliarAnalitos,
} from '@/api/generated/admin-catalogo-exames/admin-catalogo-exames';
import type { CatalogoExameDetalheResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { ApelidosDoAnalito } from '@/features/admin/catalogo-exames/components/ApelidosDoAnalito';
import { CelulaDeFaixa } from '@/features/admin/catalogo-exames/components/CelulaDeFaixa';
import {
  aplicarModelo,
  especiesEmUso,
  faixasDaEspecie,
  MODELOS,
  paraPayload,
  paraRascunho,
  type AnalitoRascunho,
  type FaixaRascunho,
} from '@/features/admin/catalogo-exames/rascunho';
import { ESPECIES, ROTULO_ESPECIE, type Especie } from '@/features/admin/catalogo-exames/rotulos';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

interface Props {
  exame: CatalogoExameDetalheResponse;
  podeEditar: boolean;
  /** Abre o diálogo completo de uma faixa (sexo, idade, texto qualitativo). */
  aoAbrirDetalheDaFaixa: (analitoId: string, faixa?: { id: string }) => void;
}

/**
 * A grade: linhas são analitos, colunas são espécies.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Substitui o par de diálogos (um por analito, outro por faixa) porque a conta
 *  não fechava: 19 exames multi-analito × 2–3 analitos × N espécies davam
 *  centenas de idas e voltas, e catálogo preenchido pela metade é laudo sem
 *  interpretação.
 *
 *  O estado é local até o "Salvar", que manda UM `PUT` com a árvore inteira.
 *  O diálogo de faixa continua existindo para o que não cabe numa célula —
 *  e fica BLOQUEADO enquanto há alteração pendente, porque as duas telas
 *  escrevendo o mesmo analito ao mesmo tempo é como uma sobrescreve a outra.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function GradeDeFaixas({ exame, podeEditar, aoAbrirDetalheDaFaixa }: Props) {
  const queryClient = useQueryClient();
  const salvar = useCatalogoExamesControllerReconciliarAnalitos();

  const doServidor = useMemo(() => paraRascunho(exame.analitos), [exame.analitos]);
  const [rascunho, setRascunho] = useState<AnalitoRascunho[]>(doServidor);
  const [colunasExtras, setColunasExtras] = useState<Especie[]>([]);

  // Recarregou do servidor (outro diálogo salvou, ou o Salvar daqui): o
  // rascunho volta a espelhar o que está gravado.
  useEffect(() => setRascunho(doServidor), [doServidor]);

  const sujo = useMemo(
    () => JSON.stringify(rascunho) !== JSON.stringify(doServidor),
    [rascunho, doServidor],
  );

  const colunas = useMemo(() => {
    const emUso = especiesEmUso(rascunho, ESPECIES);
    const todas = new Set<Especie>([...emUso, ...colunasExtras]);
    // Sem faixa nenhuma ainda: mostra as duas espécies que respondem por quase
    // todo o laboratório, senão a grade abre sem nenhuma coluna para digitar.
    if (todas.size === 0) return ['CANINO', 'FELINO'] as Especie[];
    return ESPECIES.filter((e) => todas.has(e));
  }, [rascunho, colunasExtras]);

  const semFaixa = rascunho.filter((a) => a.ativo && a.faixas.length === 0).length;

  function trocarPosicao(de: number, para: number) {
    setRascunho((atual) => {
      const copia = [...atual];
      const [movido] = copia.splice(de, 1);
      if (movido) copia.splice(para, 0, movido);
      return copia;
    });
  }

  function mudarAnalito(indice: number, mudanca: Partial<AnalitoRascunho>) {
    setRascunho((atual) => atual.map((a, i) => (i === indice ? { ...a, ...mudanca } : a)));
  }

  /**
   * Edita a faixa simples de uma célula, criando-a se ainda não existir.
   *
   * A faixa nasce só quando algo é digitado — célula vazia não vira faixa em
   * branco (o servidor recusaria, e faixa sem valor não diz nada no laudo).
   */
  function mudarCelula(
    indice: number,
    especie: Especie,
    campo: 'minimo' | 'maximo' | 'observacao',
    valor: string,
  ) {
    setRascunho((atual) =>
      atual.map((analito, i) => {
        if (i !== indice) return analito;

        const existente = analito.faixas.find((f) => f.especie === especie);
        if (existente) {
          return {
            ...analito,
            faixas: analito.faixas.map((f) => (f === existente ? { ...f, [campo]: valor } : f)),
          };
        }

        const nova: FaixaRascunho = {
          especie,
          sexo: null,
          idadeMinMeses: null,
          idadeMaxMeses: null,
          minimo: '',
          maximo: '',
          textoReferencia: '',
          observacao: '',
          [campo]: valor,
        };
        return { ...analito, faixas: [...analito.faixas, nova] };
      }),
    );
  }

  async function persistir() {
    try {
      await salvar.mutateAsync({ id: exame.id, data: { analitos: paraPayload(rascunho) } });
      await queryClient.invalidateQueries({
        queryKey: getCatalogoExamesControllerObterQueryKey(exame.id),
      });
      toast.success('Portfólio atualizado.');
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível salvar. Nada foi alterado.',
      );
    }
  }

  const ativos = rascunho.filter((a) => a.ativo);
  const inativos = rascunho.filter((a) => !a.ativo);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Analitos</h3>
          {semFaixa > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-500/40 gap-1">
              <AlertTriangle className="w-3 h-3" />
              {semFaixa} sem faixa
            </Badge>
          )}
        </div>

        {podeEditar && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Aplicar modelo
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {MODELOS.map((modelo) => (
                  <DropdownMenuItem
                    key={modelo.rotulo}
                    onClick={() => setRascunho((atual) => aplicarModelo(atual, modelo.analitos))}
                  >
                    {modelo.rotulo}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setRascunho((atual) => [
                  ...atual,
                  { nome: '', unidade: '', apelidosVertis: [], ativo: true, faixas: [] },
                ])
              }
            >
              <Plus className="w-4 h-4" />
              Analito
            </Button>

            <Button
              variant="ghost"
              size="sm"
              disabled={!sujo || salvar.isPending}
              onClick={() => setRascunho(doServidor)}
            >
              <Undo2 className="w-4 h-4" />
              Descartar
            </Button>

            <Button size="sm" disabled={!sujo || salvar.isPending} onClick={() => void persistir()}>
              {salvar.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand/10 text-left">
              <th className="px-4 py-2 font-medium text-muted-foreground min-w-56">Analito</th>
              <th className="px-2 py-2 font-medium text-muted-foreground w-28">Unidade</th>
              {colunas.map((especie) => (
                <th key={especie} className="px-2 py-2 font-medium text-muted-foreground min-w-44">
                  {ROTULO_ESPECIE[especie]}
                </th>
              ))}
              <th className="px-2 py-2 w-32">
                {podeEditar && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-xs">
                        <Plus className="w-3.5 h-3.5" />
                        Espécie
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {ESPECIES.filter((e) => !colunas.includes(e)).map((especie) => (
                        <DropdownMenuItem
                          key={especie}
                          onClick={() => setColunasExtras((atual) => [...atual, especie])}
                        >
                          {ROTULO_ESPECIE[especie]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </th>
            </tr>
          </thead>

          <tbody>
            {ativos.length === 0 && (
              <tr>
                <td colSpan={colunas.length + 3} className="px-4 py-6 text-muted-foreground">
                  Nenhum analito. Sem analito, o exame não rende nenhuma linha de resultado no laudo.
                </td>
              </tr>
            )}

            {rascunho.map((analito, indice) => {
              if (!analito.ativo) return null;

              return (
                <tr key={analito.id ?? `novo-${indice}`} className="border-b border-brand/5">
                  <td className="px-4 py-2 align-top">
                    <Input
                      aria-label={`Nome do analito ${indice + 1}`}
                      className="h-8 text-sm"
                      placeholder="Ex.: RESULTADO BASAL"
                      disabled={!podeEditar}
                      value={analito.nome}
                      onChange={(e) => mudarAnalito(indice, { nome: e.target.value })}
                    />
                    <ApelidosDoAnalito
                      nomeDoAnalito={analito.nome}
                      apelidos={analito.apelidosVertis}
                      somenteLeitura={!podeEditar}
                      aoMudar={(apelidos) => mudarAnalito(indice, { apelidosVertis: apelidos })}
                    />
                    {analito.faixas.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Sem faixa: o resultado sai como indeterminado.
                      </p>
                    )}
                  </td>

                  <td className="px-2 py-2 align-top">
                    <Input
                      aria-label={`Unidade do analito ${indice + 1}`}
                      className="h-8 text-sm"
                      placeholder="µg/dL"
                      disabled={!podeEditar}
                      value={analito.unidade}
                      onChange={(e) => mudarAnalito(indice, { unidade: e.target.value })}
                    />
                  </td>

                  {colunas.map((especie) => (
                    <td key={especie} className="px-2 py-2 align-top">
                      <CelulaDeFaixa
                        faixas={faixasDaEspecie(analito, especie)}
                        unidade={analito.unidade}
                        somenteLeitura={!podeEditar}
                        detalheBloqueado={sujo || !analito.id}
                        aoMudar={(campo, valor) => mudarCelula(indice, especie, campo, valor)}
                        aoAbrirDetalhe={() => {
                          const primeira = faixasDaEspecie(analito, especie)[0];
                          if (analito.id && primeira?.id) {
                            aoAbrirDetalheDaFaixa(analito.id, { id: primeira.id });
                          }
                        }}
                      />
                    </td>
                  ))}

                  <td className="px-2 py-2 align-top text-right whitespace-nowrap">
                    {podeEditar && (
                      <>
                        {/* A ordem da lista É a ordem do laudo impresso (o
                            servidor usa a posição quando não vem `ordem`), então
                            reordenar aqui é o que substitui o campo numérico do
                            antigo diálogo de analito. */}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Subir ${analito.nome || `analito ${indice + 1}`}`}
                          disabled={indice === 0}
                          onClick={() => trocarPosicao(indice, indice - 1)}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Descer ${analito.nome || `analito ${indice + 1}`}`}
                          disabled={indice === rascunho.length - 1}
                          onClick={() => trocarPosicao(indice, indice + 1)}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remover ${analito.nome || `analito ${indice + 1}`}`}
                          className="text-destructive hover:text-destructive"
                          onClick={() => mudarAnalito(indice, { ativo: false })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {inativos.length > 0 && (
        <div className="text-xs text-muted-foreground space-y-1">
          {/* Desativado continua aqui porque há laudo apontando para ele — o
              analito nunca é apagado de verdade. */}
          <p className="font-medium">Desativados</p>
          {rascunho.map((analito, indice) =>
            analito.ativo ? null : (
              <div key={analito.id ?? `inativo-${indice}`} className="flex items-center gap-2">
                <span className="line-through">{analito.nome}</span>
                {podeEditar && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => mudarAnalito(indice, { ativo: true })}
                  >
                    Reativar
                  </Button>
                )}
              </div>
            ),
          )}
        </div>
      )}

      {sujo && (
        <p className="text-xs text-amber-600">
          Há alterações não salvas. O diálogo de faixa por sexo/idade fica indisponível até salvar.
        </p>
      )}
    </div>
  );
}
